import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronRight, Home } from 'lucide-react';
import { getColeccionBySlug } from '../data/colecciones';
import Navbar from '../components/Navbar';
import SEOHead from '../components/SEOHead';
import { LINKEDIN_PROFILE_URL } from '../constants/links';

const COL_SLUG = 'ado';

/* ── Estado inline ────────────────────────────────────────────── */
const EstadoDot = ({ estado }) => {
  const map = {
    publicado: { dot: 'bg-emerald-500', label: 'Publicado' },
    proximamente: { dot: 'bg-slate-300', label: 'Próximamente' },
  };
  const { dot, label } = map[estado] || map.proximamente;
  return (
    <span className="flex items-center gap-1.5 text-xs text-[#8793A3] font-medium">
      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${dot}`} />
      {label}
    </span>
  );
};

/* ── Descripciones de conceptos ──────────────────────────── */
const CONCEPT_DESCRIPTIONS = {
  'Arquitectura de decisión organizacional': 'Conjunto de condiciones que determina cómo una organización protege, evalúa y sostiene iniciativas estratégicas.',
  'Iniciativa exploratoria': 'Iniciativa que opera bajo incertidumbre y necesita aprender antes de poder demostrar resultados comparables con el negocio actual.',
  'Perfil explorador': 'Conjunto de características que definen quién puede liderar iniciativas exploratorias.',
  'Disolución silenciosa': 'Desaparición progresiva de una iniciativa sin una decisión formal de cancelación.',
  'Absorción operativa': 'Captura gradual de personas, tiempo y recursos de una iniciativa por las urgencias de la operación.',
  'Momentum institucional': 'Impulso acumulado que mantiene la visibilidad de una iniciativa en ausencia de protección formal.',
  'Caducidad prematura': 'Evaluación o cierre de una iniciativa con criterios no adecuados a su etapa exploratoria.',
  'Inercia por omisión': 'Continuación aparente de una iniciativa sin recursos ni atención real.',
  'Redistribución silenciosa': 'Reasignación progresiva de recursos hacia la operación sin ninguna decisión visible.',
  'Patrocinio informal': 'Apoyo personal de un líder que sostiene una iniciativa sin vínculo institucional formal.',
};

/* ── Portada visual ADO ────────────────────────────────────── */
const ADOCover = () => (
  <div className="relative bg-[#0c203b] rounded-xl overflow-hidden flex flex-col justify-between p-7 sm:p-8 min-h-[220px] lg:h-full">
    <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
      <svg viewBox="0 0 400 480" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
        <line x1="200" y1="0" x2="200" y2="480" stroke="#8DB8D4" strokeWidth="0.75" strokeOpacity="0.08"/>
        <line x1="0" y1="240" x2="400" y2="240" stroke="#8DB8D4" strokeWidth="0.75" strokeOpacity="0.08"/>
        <rect x="32" y="32" width="336" height="416" stroke="#8DB8D4" strokeWidth="0.75" strokeOpacity="0.07" fill="none"/>
        <rect x="80" y="88" width="240" height="304" stroke="#C4933A" strokeWidth="0.75" strokeOpacity="0.12" fill="none"/>
        <line x1="32" y1="240" x2="80" y2="240" stroke="#C4933A" strokeWidth="1" strokeOpacity="0.18"/>
        <line x1="320" y1="240" x2="368" y2="240" stroke="#C4933A" strokeWidth="1" strokeOpacity="0.18"/>
      </svg>
    </div>
    <div className="mb-auto">
      <p className="text-[10px] font-semibold tracking-[0.22em] text-white/30 uppercase">
        Las 4 condiciones de supervivencia
      </p>
    </div>
    <div className="space-y-3.5">
      {[
        { num: '01', label: 'Espacio estructural' },
        { num: '02', label: 'Autoridad de decisión' },
        { num: '03', label: 'Protección de recursos' },
        { num: '04', label: 'Horizonte de maduración' },
      ].map((d) => (
        <div key={d.num} className="flex items-center gap-3">
          <span className="text-[10px] font-bold text-amber-400/60 tracking-widest w-5 flex-shrink-0">{d.num}</span>
          <span className="text-sm text-white/70 font-medium">{d.label}</span>
        </div>
      ))}
    </div>
  </div>
);

/* ── Componente principal ─────────────────────────────────────── */
const READING_STAGES = [
  { etapa: '01', titulo: 'EL FENÓMENO', descripcion: 'Reconocer por qué una iniciativa necesita un espacio organizacional propio y quién debe sostenerla.', numeros: ['00', '01'] },
  { etapa: '02', titulo: 'CÓMO OCURRE', descripcion: 'Observar cómo una iniciativa puede desaparecer sin una decisión formal de cancelación.', numeros: ['02', '03'] },
  { etapa: '03', titulo: 'POR QUÉ LAS SOLUCIONES HABITUALES NO FUNCIONAN', descripcion: 'Comprender por qué mejores datos, una presentación más sólida o un patrocinador individual no sustituyen una arquitectura de decisión.', numeros: ['04', '05'] },
  { etapa: '04', titulo: 'LA ARQUITECTURA', descripcion: 'Integrar las condiciones que permiten que una iniciativa exploratoria tenga posibilidades reales de avanzar.', numeros: ['06'] },
];

const FEATURED_CONCEPTS = [
  'Disolución silenciosa',
  'Absorción operativa',
  'Arquitectura de decisión organizacional',
  'Iniciativa exploratoria',
];

const ColeccionADO = () => {
  const col = getColeccionBySlug(COL_SLUG);
  if (!col) return null;

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="ADO — Arquitectura de Decisión Organizacional | Edutech Pulse Lab"
        description="Una colección sobre cómo las organizaciones deciden qué iniciativas reciben recursos, cuáles son absorbidas por la operación y cuáles desaparecen antes de demostrar su valor."
        keywords="arquitectura decision organizacional, ADO, innovación corporativa, gestión iniciativas, por qué la innovación no avanza"
        canonicalUrl="/colecciones/ado"
      />
      <Navbar />

      {/* ── Hero ────────────────────────────────────────────────── */}
      <section className="bg-[#0c203b] text-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 pt-9 pb-10 sm:pt-12 sm:pb-14 lg:pt-16 lg:pb-16">
          {/* Breadcrumb */}
          <nav className="flex flex-wrap items-center gap-1.5 text-xs text-[#7F8B99] mb-8">
            <Link to="/" className="hover:text-white/70 transition-colors flex items-center gap-1">
              <Home className="w-3 h-3" />
              Inicio
            </Link>
            <ChevronRight className="w-3 h-3" />
            <Link to="/colecciones" className="hover:text-white/70 transition-colors">
              Colecciones
            </Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-[#AEB8C4]/70">ADO</span>
          </nav>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12 items-start">
            <div className="lg:col-span-3">
              <h1 className="text-[2rem] sm:text-[2.5rem] lg:text-[3rem] font-bold leading-[1.08] tracking-tight text-white mb-3">
                {col.nombreCompleto}
              </h1>
              <p className="text-sm text-white/45 mb-5 font-normal">
                Una colección de{' '}
                <a
                  href={LINKEDIN_PROFILE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white/70 hover:text-white transition-colors underline underline-offset-2"
                  aria-label="Ver el perfil de David Prudencio en LinkedIn"
                >
                  David Prudencio
                </a>
              </p>
              <p className="text-white/70 text-base sm:text-lg leading-[1.65] max-w-xl font-normal">
                {col.tagline}
              </p>

              {/* Métricas */}
              <div className="flex items-center gap-6 sm:gap-8 mt-8">
                <div>
                  <p className="text-2xl sm:text-3xl font-bold text-white">{col.articuloCount}</p>
                  <p className="text-xs text-white/40 font-medium tracking-wide uppercase mt-1">Artículos</p>
                </div>
                <div className="w-px h-8 bg-white/10" />
                <div>
                  <p className="text-2xl sm:text-3xl font-bold text-white">{col.conceptoCount}</p>
                  <p className="text-xs text-white/40 font-medium tracking-wide uppercase mt-1">Conceptos</p>
                </div>
              </div>

              {/* Portada en móvil */}
              <div className="lg:hidden mt-8">
                <ADOCover />
              </div>
            </div>

            {/* Portada desktop */}
            <div className="hidden lg:block lg:col-span-2">
              <ADOCover />
            </div>
          </div>
        </div>
      </section>

      {/* ── Descripción ─────────────────────────────────────────── */}
      <section className="border-b border-[#E5E9EF]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-10 sm:py-14">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 lg:gap-16">
            <div className="lg:col-span-2 space-y-4">
              <p className="text-xs font-semibold tracking-[0.2em] text-slate-500 uppercase mb-6">
                De qué trata
              </p>
              {col.descripcionLarga.split('\n\n').map((para, i) => (
                <p key={i} className="text-[#3D4A5A] text-base sm:text-lg leading-[1.7] font-normal">
                  {para}
                </p>
              ))}
            </div>

            <div className="space-y-5">
              <div>
                <p className="text-xs font-semibold tracking-[0.15em] text-slate-500 uppercase mb-3">
                  Para quién
                </p>
                <p className="text-[#3D4A5A] text-sm leading-relaxed font-normal">{col.paraQuien}</p>
              </div>
              <div className="w-full h-px bg-[#E5E9EF]" />
              <div>
                <p className="text-xs font-semibold tracking-[0.15em] text-slate-500 uppercase mb-3">
                  Qué problema aborda
                </p>
                <p className="text-[#3D4A5A] text-sm leading-relaxed font-normal">{col.queAborda}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Ruta de lectura */}
      <section className="border-b border-[#E5E9EF]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-10 sm:py-14 lg:py-16">
          <p className="text-xs font-semibold tracking-[0.2em] text-slate-500 uppercase mb-4">
            Ruta de lectura
          </p>
          <p className="text-[#3D4A5A] text-base sm:text-lg leading-[1.7] mb-10 max-w-2xl font-normal">
            Siete artículos organizados en cuatro etapas para comprender cómo una iniciativa pierde condiciones de supervivencia y qué arquitectura necesita para avanzar.
          </p>
          <div className="space-y-10 sm:space-y-12">
            {READING_STAGES.map((stage) => {
              const arts = stage.numeros
                .map((n) => col.articulos.find((a) => a.numero === n))
                .filter(Boolean);
              return (
                <div key={stage.etapa}>
                  <div className="mb-8">
                    <div className="flex items-center gap-3 mb-2.5">
                      <span className="text-[11px] font-bold text-amber-500 tracking-[0.18em] uppercase">Etapa {stage.etapa}</span>
                      <span className="w-px h-4 bg-slate-200" />
                      <p className="text-[13px] sm:text-[14px] font-bold tracking-[0.06em] text-[#172033] uppercase">{stage.titulo}</p>
                    </div>
                    {stage.descripcion && (
                      <p className="text-[15px] sm:text-[16px] text-[#3D4A5A] leading-[1.65] max-w-xl mt-1 font-normal">{stage.descripcion}</p>
                    )}
                  </div>
                  <div className="divide-y divide-[#E5E9EF] border border-[#E5E9EF] rounded-xl overflow-hidden">
                    {arts.map((art) => {
                      const isPublicado = art.estado === 'publicado' && art.slug;
                      const artUrl = `/colecciones/ado/${art.slug}`;
                      const Row = (
                        <div className={`px-5 py-5 sm:px-7 sm:py-6 bg-white transition-colors ${isPublicado ? 'hover:bg-slate-50 group cursor-pointer' : ''}`}>
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-1.5">
                                <span className="text-xs font-medium text-[#A0A0A0]">ADO·{art.numero}</span>
                                {art.readTime && (
                                  <>
                                    <span className="w-1 h-1 rounded-full bg-[#D5DCE5]" />
                                    <span className="text-xs text-[#8793A3]">{art.readTime}</span>
                                  </>
                                )}
                              </div>
                              <p className={`text-[15px] sm:text-[17px] font-semibold leading-snug tracking-[-0.01em] ${isPublicado ? 'text-[#172033] group-hover:text-[#4C586A]' : 'text-[#172033]'}`}>
                                {art.titulo}
                              </p>
                            </div>
                            {isPublicado && (
                              <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-[#4C586A] flex-shrink-0 mt-0.5 transition-colors" />
                            )}
                          </div>
                        </div>
                      );
                      return isPublicado ? (
                        <Link key={art.numero} to={artUrl} className="block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#4C586A]">{Row}</Link>
                      ) : (
                        <div key={art.numero}>{Row}</div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Framework ADO */}
      <section className="border-b border-[#E5E9EF]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-10 sm:py-14 lg:py-16">
          <p className="text-xs font-semibold tracking-[0.2em] text-slate-500 uppercase mb-4">
            Framework ADO
          </p>
          <p className="text-[#3D4A5A] text-base sm:text-lg leading-[1.7] mb-10 max-w-2xl font-normal">
            Una iniciativa exploratoria necesita cuatro condiciones simultáneas. La ausencia de una sola puede producir disolución silenciosa.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-[#E5E9EF] rounded-2xl overflow-hidden">
            {[
              { num: '01', dimension: 'Espacio estructural', pregunta: '¿Dónde vive la iniciativa y qué lugar ocupa fuera de la presión inmediata de la operación?', sintoma: 'Absorción operativa' },
              { num: '02', dimension: 'Autoridad de decisión', pregunta: '¿Quién puede decidir cuando las necesidades de la exploración compiten con las prioridades del negocio actual?', sintoma: 'Inercia por omisión' },
              { num: '03', dimension: 'Protección de recursos', pregunta: '¿Qué personas, tiempo, presupuesto y capacidades están realmente protegidos para sostener la iniciativa?', sintoma: 'Redistribución silenciosa' },
              { num: '04', dimension: 'Horizonte de maduración', pregunta: '¿Con qué métricas, evidencia y plazo será evaluada antes de exigirle resultados de una operación madura?', sintoma: 'Caducidad prematura' },
            ].map((d) => (
              <div key={d.num} className="bg-white p-7 sm:p-8">
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-xs font-bold text-amber-500 tracking-widest">{d.num}</span>
                  <span className="w-px h-3 bg-slate-200" />
                  <span className="text-xs font-semibold tracking-[0.12em] text-slate-500 uppercase">{d.dimension}</span>
                </div>
                <p className="text-base sm:text-[17px] text-[#3D4A5A] leading-[1.65] font-normal">{d.pregunta}</p>
                <div className="mt-5 pt-5 border-t border-[#E5E9EF]">
                  <p className="text-xs text-[#8793A3] font-medium mb-2">Cuando falta:</p>
                  <span className="inline-block text-xs text-[#4C586A] bg-slate-50 border border-slate-200 rounded px-2.5 py-1 font-medium">{d.sintoma}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* ── Conceptos ───────────────────────────────────────────── */}
      <section className="border-b border-[#E5E9EF]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-10 sm:py-14">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 lg:gap-16">
            <div>
              <p className="text-xs font-semibold tracking-[0.2em] text-slate-500 uppercase mb-3">
                Conceptos ADO
              </p>
              <p className="text-[#3D4A5A] text-sm leading-relaxed mb-5 font-normal">
                Un vocabulario para nombrar patrones y mecanismos organizacionales que suelen permanecer invisibles.
              </p>
              <Link
                to="/colecciones/ado/conceptos"
                className="flex items-center gap-1.5 text-sm font-semibold text-[#172033] hover:text-[#4C586A] transition-colors"
              >
                Ver los {col.conceptoCount} conceptos
                <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
            <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {FEATURED_CONCEPTS.map((c) => (
                <div key={c} className="border border-[#E5E9EF] rounded-xl p-4 bg-white">
                  <p className="text-sm font-semibold text-[#172033] mb-1.5">{c}</p>
                  <p className="text-xs text-[#3D4A5A] leading-relaxed font-normal">{CONCEPT_DESCRIPTIONS[c] || ''}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ─────────────────────────────────────────────────── */}
      {/* Sobre el autor */}
      <section className="border-b border-[#E5E9EF] bg-slate-50/60">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-10 sm:py-14">
          <p className="text-xs font-semibold tracking-[0.2em] text-slate-500 uppercase mb-8">
            Sobre el autor
          </p>
          <div className="flex flex-col sm:flex-row gap-8 items-start">
            {/* Foto del autor — colocar en /public/images/autores/david-prudencio.webp */}
            <div className="relative w-24 h-24 sm:w-28 sm:h-28 flex-shrink-0">
              {/* Placeholder con iniciales, visible mientras no haya foto */}
              <div className="absolute inset-0 rounded-full bg-[#0c203b] border border-[#E5E9EF] flex items-center justify-center" aria-hidden="true">
                <span className="text-white text-xl font-bold tracking-widest select-none">DP</span>
              </div>
              <img
                src="/images/autores/david-prudencio.png"
                alt="David Prudencio, autor de Arquitectura de Decisión Organizacional"
                className="absolute inset-0 w-full h-full rounded-full object-cover border border-[#E5E9EF]"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-bold text-[#172033] mb-2">David Prudencio</h3>
              <p className="text-[#3D4A5A] text-base leading-relaxed mb-6 max-w-lg font-normal">
                Líder de innovación y transformación digital. Creador de Arquitectura de Decisión Organizacional y fundador de Edutech Pulse Lab.
              </p>
              <a
                href={LINKEDIN_PROFILE_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Ver el perfil de David Prudencio en LinkedIn"
                className="inline-flex items-center gap-2 text-sm font-semibold text-[#172033] border border-[#E5E9EF] hover:border-[#4C586A] px-5 py-2.5 rounded-lg transition-colors"
              >
                Conectar en LinkedIn
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0c203b]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-12 sm:py-16">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white leading-tight mb-4 max-w-lg">
            ¿Quieres seguir explorando ADO?
          </h2>
          <p className="text-[#AEB8C4] text-base leading-relaxed mb-8 max-w-xl">
            Comienza por el primer artículo de la serie o vuelve al índice general de colecciones.
          </p>
          <div className="flex flex-col sm:flex-row items-start gap-3">
            <Link
              to="/colecciones/ado/ado-00-la-innovacion-necesita-un-lugar"
              className="group inline-flex items-center gap-2 px-7 py-3.5 bg-white text-[#081827] font-semibold rounded-xl hover:bg-slate-100 transition-colors text-sm"
            >
              Comenzar con ADO·00
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </Link>
            <Link
              to="/colecciones"
              className="inline-flex items-center gap-2 px-7 py-3.5 border border-white/20 text-white/70 hover:text-white hover:border-white/40 font-medium rounded-xl transition-colors text-sm"
            >
              Ver todas las colecciones
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ColeccionADO;
